import React, { useEffect, useRef } from "react";

export const key = "particle-network";

const DEFAULT_PARTICLE_COLOR = "#60a5fa";
const DEFAULT_LINE_COLOR = "#93c5fd";
const DEFAULT_BG_COLOR = "#0f172a";

const PARTICLE_COUNT_DENSITY = 0.00012;
const MAX_PARTICLES = 140;
const MIN_PARTICLES = 24;

const LINK_DISTANCE = 130;
const PARTICLE_RADIUS = 1.8;
const SPEED = 0.25;

function pickColor(colors, idx, fallback) {
  if (!Array.isArray(colors) || !colors.length) return fallback;
  return colors[idx] || colors[colors.length - 1] || fallback;
}

function hexToRgba(hex, alpha) {
  if (!hex || typeof hex !== "string" || hex[0] !== "#") return `rgba(255,255,255,${alpha})`;
  let value = hex.slice(1);
  if (value.length === 3) {
    value = value.split("").map((c) => c + c).join("");
  }
  if (value.length !== 6) return `rgba(255,255,255,${alpha})`;
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export default function ParticleNetwork({ colors = [], style }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const particlesRef = useRef([]);
  const colorsRef = useRef({
    background: DEFAULT_BG_COLOR,
    particle: DEFAULT_PARTICLE_COLOR,
    line: DEFAULT_LINE_COLOR,
  });

  useEffect(() => {
    colorsRef.current = {
      background: pickColor(colors, 0, DEFAULT_BG_COLOR),
      particle: pickColor(colors, 1, DEFAULT_PARTICLE_COLOR),
      line: pickColor(colors, 2, pickColor(colors, 1, DEFAULT_LINE_COLOR)),
    };
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    let width = 0;
    let height = 0;
    let dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      seedParticles();
    };

    const seedParticles = () => {
      const target = Math.round(width * height * PARTICLE_COUNT_DENSITY);
      const count = Math.max(MIN_PARTICLES, Math.min(MAX_PARTICLES, target));
      const particles = [];
      for (let i = 0; i < count; i += 1) {
        particles.push({
          x: Math.random() * width,
          y: Math.random() * height,
          vx: (Math.random() - 0.5) * SPEED * 2,
          vy: (Math.random() - 0.5) * SPEED * 2,
        });
      }
      particlesRef.current = particles;
    };

    const tick = () => {
      const { background, particle, line } = colorsRef.current;
      ctx.fillStyle = background;
      ctx.fillRect(0, 0, width, height);

      const particles = particlesRef.current;

      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > width) p.vx *= -1;
        if (p.y < 0 || p.y > height) p.vy *= -1;
      }

      ctx.lineWidth = 1;
      for (let i = 0; i < particles.length; i += 1) {
        const a = particles[i];
        for (let j = i + 1; j < particles.length; j += 1) {
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < LINK_DISTANCE) {
            const alpha = 1 - dist / LINK_DISTANCE;
            ctx.strokeStyle = hexToRgba(line, alpha * 0.55);
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      ctx.fillStyle = particle;
      for (let i = 0; i < particles.length; i += 1) {
        const p = particles[i];
        ctx.beginPath();
        ctx.arc(p.x, p.y, PARTICLE_RADIUS, 0, Math.PI * 2);
        ctx.fill();
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
